import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'antd/lib/form/Form';
import isEmpty from 'lodash/isEmpty';
import {
  EauStatusesEnum,
  EauTypesEnum,
  PLATFORM_PERMISSIONS,
} from '@thinknet-gmbh/platform-common';

import {
  Form,
  ButtonsWrapper,
  Button,
  TextInput,
  SelectInput,
  DatePickerInput,
  UploadInput,
  Typography,
  PLATFORM_THEME_COLORS,
  Icon,
  ICONS,
  CheckboxInput,
  TextAreaInput,
} from '@thinknet-gmbh/platform-common-ui';

import { useUserCanManageDocument } from 'src/hooks/user-can-manage-document.hook';
import { useGetCurrentMemberAccount } from 'src/hooks/accounts/get-current-member-account.hook';
import { useGetAllEmployeesForList } from 'src/hooks/employees/get-all-employees-for-list.hook';
import { useGetAllHealthInsurance } from 'src/hooks/health-insurance/get-all-health-insurance.hook';
import { useGetEuaCompanySetting } from 'src/hooks/applications-setting/get-application-company-setting.hook';

import { EauFormWrapper, LabelFileInfo } from './eau-form.styles';
import { IEau } from '../../../../eau.interfaces';

const EauForm = ({
  onClose,
  onFinishHandler,
  initialValues,
  loading,
  isEdit,
  companyId,
  accountId,
}: {
  loading?: boolean;
  isEdit?: boolean;
  onClose: () => void;
  onFinishHandler: (values: any) => void;
  initialValues: IEau;
  companyId?: string;
  accountId?: string;
}) => {
  const { t, i18n } = useTranslation(['applications', 'common']);

  const [form] = useForm();

  const { canEdit, canEditALl } = useUserCanManageDocument({
    platformPermission: PLATFORM_PERMISSIONS.applications,
    createdBy: accountId,
  });

  const { euaSetting } = useGetEuaCompanySetting(companyId);

  const isEditOwnPermission = canEdit && !canEditALl;

  const { member = {} } = useGetCurrentMemberAccount();

  const { employees } = useGetAllEmployeesForList({
    filter: { companyId, accountActive: true },
  });

  const employeesOptions = isEditOwnPermission ? [member] : employees;

  const { healthInsurance } = useGetAllHealthInsurance();

  const healthInsuranceOptions = healthInsurance.map((el: { name: string; _id: string }) => ({
    value: el._id,
    label: el.name,
  }));

  useEffect(() => {
    if (isEditOwnPermission && !isEdit) {
      form.setFieldsValue({
        employeeId: member?._id,
        employeeNumber: member?.employeePersonalInfo?.employeeNumber,
        healthInsuranceId: member?.healthInsurance?._id,
      });
    }
  }, [
    isEditOwnPermission,
    isEdit,
    member?._id,
    form,
    member?.employeePersonalInfo?.employeeNumber,
    member?.healthInsurance?._id,
  ]);

  return (
    <EauFormWrapper
      form={form}
      layout="vertical"
      onFinish={values => onFinishHandler(values)}
      initialValues={initialValues}
    >
      <Form.Item noStyle shouldUpdate>
        {({ setFieldsValue }) => {
          return (
            <Form.Item
              name="employeeId"
              label={t('applications:eAU.labels.employee')}
              rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
            >
              <SelectInput
                disabled={isEditOwnPermission || isEdit}
                placeholder={t('applications:eAU.labels.selectEmployee')}
                onSelect={id => {
                  const emp = employeesOptions?.find((el: any) => String(el?._id) === String(id));
                  setFieldsValue({
                    employeeId: id,
                    employeeNumber: emp?.employeePersonalInfo?.employeeNumber,
                    healthInsuranceId: emp?.healthInsurance?._id,
                  });
                }}
              >
                {(!isEmpty(employeesOptions) ? employeesOptions : []).map(
                  ({ _id, profile }: any) => (
                    <SelectInput.Option key={_id} value={_id}>
                      {profile?.displayName}
                    </SelectInput.Option>
                  )
                )}
              </SelectInput>
            </Form.Item>
          );
        }}
      </Form.Item>

      <Form.Item
        name="employeeNumber"
        label={t('applications:eAU.labels.employeeNumber')}
        rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
      >
        <TextInput placeholder={'1644'} disabled />
      </Form.Item>

      <Form.Item
        name="healthInsuranceId"
        label={t('applications:eAU.labels.healthInsurance')}
        rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
      >
        <SelectInput disabled placeholder="Barmer GEK">
          {(!isEmpty(healthInsuranceOptions) ? healthInsuranceOptions : []).map(
            ({ value, label }: any) => (
              <SelectInput.Option key={value} value={value}>
                {label}
              </SelectInput.Option>
            )
          )}
        </SelectInput>
      </Form.Item>

      <Form.Item
        name={'firstDay'}
        label={t('applications:eAU.labels.firstDaySickLeave')}
        rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
      >
        <DatePickerInput lang={i18n.language} format="DD.MM.YYYY" disabled={isEdit} />
      </Form.Item>

      <Form.Item
        name={'lastDay'}
        label={t('applications:eAU.labels.lastDaySickLeave')}
        rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
      >
        <DatePickerInput lang={i18n.language} format="DD.MM.YYYY" disabled={isEdit} />
      </Form.Item>

      <Form.Item
        name={'eauType'}
        label={t('applications:eAU.labels.eAUType')}
        rules={[
          {
            required: true,
            message: t('common:validation.error.fieldRequired'),
          },
        ]}
      >
        <SelectInput disabled={isEdit} placeholder={t('applications:eAU.labels.selectEau')}>
          {Object.keys(EauTypesEnum).map(option => (
            <SelectInput.Option value={option} key={option}>
              {t(`applications:eAU.eAUTypes.${option}`)}
            </SelectInput.Option>
          ))}
        </SelectInput>
      </Form.Item>

      {isEdit && (
        <Fragment>
          <Form.Item noStyle shouldUpdate>
            {({ setFieldsValue }) => {
              return (
                <Form.Item
                  name={'status'}
                  label={t('applications:eAU.labels.status')}
                  rules={[
                    {
                      required: true,
                      message: t('common:validation.error.fieldRequired'),
                    },
                  ]}
                >
                  <SelectInput
                    onSelect={status => {
                      if (String(status) !== String(EauStatusesEnum.rejected)) {
                        setFieldsValue({
                          rejectReason: '',
                          rejectDetails: '',
                          status: status,
                        });
                      }
                      setFieldsValue({
                        status: status,
                      });
                    }}
                  >
                    {Object.keys(EauStatusesEnum).map(option => (
                      <SelectInput.Option value={option} key={option}>
                        {t(`applications:eAU.statuses.${option?.toLowerCase()}`)}
                      </SelectInput.Option>
                    ))}
                  </SelectInput>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => {
              const status = getFieldValue('status');
              return (
                <Fragment>
                  {String(status) === String(EauStatusesEnum.rejected) && (
                    <Fragment>
                      <Form.Item
                        name="rejectReason"
                        label={t('applications:eAU.labels.rejectReason')}
                      >
                        <SelectInput
                          onSelect={reason => {
                            const reasonValue = euaSetting?.rejectReasons?.find(
                              (el: any) => reason === el?.reason
                            );
                            setFieldsValue({
                              rejectReason: reason,
                              rejectDetails: reasonValue?.rejectValue || '',
                            });
                          }}
                        >
                          {(!isEmpty(euaSetting?.rejectReasons)
                            ? euaSetting?.rejectReasons
                            : []
                          ).map(({ reason }: any) => (
                            <SelectInput.Option key={reason} value={reason}>
                              {reason}
                            </SelectInput.Option>
                          ))}
                        </SelectInput>
                      </Form.Item>
                      <Form.Item
                        name="rejectDetails"
                        label={t('applications:eAU.labels.rejectDetails')}
                        rules={[
                          {
                            required: String(status) === String(EauStatusesEnum.rejected),
                            message: t('common:validation.error.fieldRequired'),
                          },
                        ]}
                      >
                        <TextAreaInput showCount maxLength={500} />
                      </Form.Item>
                    </Fragment>
                  )}
                </Fragment>
              );
            }}
          </Form.Item>
        </Fragment>
      )}

      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue, setFieldsValue }) => {
          const { fileList, file } = getFieldValue('fileId') || {};

          return (
            <Form.Item
              name="fileId"
              className={'upload-file-field'}
              valuePropName="fileId"
              label={t('applications:eAU.labels.upload')}
            >
              {isEmpty(fileList) ? (
                <UploadInput
                  showUploadList={false}
                  disabled={isEdit}
                  beforeUpload={() => false}
                  variant="button"
                  multiple={false}
                  btnText={t('applications:eAU.labels.uploadFile')}
                  accept=".jpeg, .jpg, .png, .pdf"
                  maxCount={1}
                  defaultFileList={undefined}
                />
              ) : (
                <LabelFileInfo>
                  <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                    <a target="_blank" rel="noopener noreferrer" href={file?.url} title={'file'}>
                      {file?.name}
                    </a>
                  </Typography>
                  {!isEdit && (
                    <Icon
                      onClick={() => {
                        setFieldsValue({
                          fileId: undefined,
                        });
                      }}
                      icon={ICONS.trash}
                      color={PLATFORM_THEME_COLORS.tone.red}
                      rightMargin={false}
                    />
                  )}
                </LabelFileInfo>
              )}
            </Form.Item>
          );
        }}
      </Form.Item>

      <Form.Item
        valuePropName="checked"
        name={'confirmation'}
        rules={[
          {
            validator: (_: any, value: any) =>
              value
                ? Promise.resolve()
                : Promise.reject(new Error(t('common:validation.error.fieldRequired'))),
          },
        ]}
      >
        <CheckboxInput disabled={isEdit}>{t('applications:eAU.labels.confirm')}</CheckboxInput>
      </Form.Item>

      <ButtonsWrapper marginTop={16}>
        <Button type="primary" loading={loading} htmlType="submit">
          {t('common:buttons.save')}
        </Button>
        <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
      </ButtonsWrapper>
    </EauFormWrapper>
  );
};

export default EauForm;
